import {CSSProperties, forwardRef, useEffect, useMemo, useRef, useState} from "react";
import classNames from "classnames";

import {ManualPopover} from "@pg-design/floating-module";
import {Range, RangeColorVariant} from "@pg-design/range-module";

import {MAX_VALUE_INPUT_LIMITER} from "../../constants/max_value_input_limiter";
import {IMaxValueInputProps} from "../../types/IMaxValueInputProps";
import {getNumberFormat} from "../../utils/get_number_format";
import {Input} from "../input/Input";

import * as cns from "./MaxValueInput.module.css";

export const MaxValueInput = forwardRef<HTMLInputElement, IMaxValueInputProps>((props, forwardedRef) => {
    const {
        step,
        min,
        max,
        name,
        value,
        onChange,
        inputVariant = "row",
        isDynamicStep = false,
        defaultValue,
        inputClassName,
        maxDisplayLabel,
        centerValue,
        useTriColorSlider,
        ...inputProps
    } = props;

    const [displayValue, setDisplayValue] = useState<string>(getNumberFormat(Number(value || defaultValue || min)));
    const [warning, setWarning] = useState({isOpen: false, message: ""});
    const [inputWidth, setInputWidth] = useState(`${displayValue.toString().length}ch`);

    const warningHideRef = useRef<null | ReturnType<typeof setTimeout>>(null);
    const measureSpanRef = useRef<null | HTMLSpanElement>(null);
    const rangeValue = useMemo(() => {
        if (maxDisplayLabel && displayValue === maxDisplayLabel) {
            return {
                upper: max
            };
        }
        const noWs = getWithoutWhiteSpace(displayValue);

        return {
            upper: parseInt(noWs)
        };
    }, [displayValue]);

    useEffect(() => {
        if (maxDisplayLabel) {
            const numValue = parseInt(getWithoutWhiteSpace(displayValue));
            (numValue === max || numValue === MAX_VALUE_INPUT_LIMITER) && setDisplayValue(maxDisplayLabel);
        }
        const hiddenSpanObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setInputWidth(`${entry.contentRect.width}px`);
            }
        });

        if (measureSpanRef.current) {
            hiddenSpanObserver.observe(measureSpanRef.current);
        }

        return () => {
            hiddenSpanObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (warning.isOpen) {
            warningHideRef.current = setTimeout(() => {
                setWarning({isOpen: false, message: warning.message});
            }, 2000);
        }

        return () => {
            if (warningHideRef.current) {
                clearTimeout(warningHideRef.current);
                warningHideRef.current = null;
            }
        };
    }, [warning.isOpen]);

    useEffect(() => {
        if (onChange) {
            if (displayValue === maxDisplayLabel) {
                onChange(MAX_VALUE_INPUT_LIMITER);
                return;
            }
            const numValue = getWithoutWhiteSpace(displayValue);
            onChange(parseInt(numValue));
        }
    }, [displayValue]);

    return (
        <div
            className={classNames(cns.maxValueInput, inputVariant === "column" && cns.maxValueInputColumn)}
            style={
                {
                    "--input-width": inputWidth
                } as CSSProperties
            }
        >
            <Range
                name={`${name}_range`}
                value={rangeValue}
                useDynamicChange
                onChange={(_, value) => {
                    if (value.upper || value.upper === 0) {
                        maxDisplayLabel && value.upper === max ? setDisplayValue(maxDisplayLabel) : setDisplayValue(getNumberFormat(Number(value.upper)));
                    }
                }}
                step={step}
                min={min}
                max={max}
                className={classNames(cns.maxValueInputRange, inputProps.disabled && cns.maxValueInputRangeDisabled)}
                hideLabels
                disabled={inputProps.disabled}
                centerValue={centerValue}
                useStepChange={isDynamicStep}
                colorVariant={useTriColorSlider ? RangeColorVariant.RED_YELLOW_GREEN : RangeColorVariant.LAVENDER}
            />
            <ManualPopover
                popover={warning.message}
                isOpen={warning.isOpen}
                type="warning"
                handleSetIsOpen={(isWarningOpen) => {
                    setWarning({isOpen: isWarningOpen, message: warning.message});
                }}
                className={cns.maxValueInputPopover}
            >
                {({refs, getReferenceProps}) => (
                    <div
                        ref={refs.setReference}
                        {...getReferenceProps()}
                        className={inputVariant === "column" ? cns.maxValueInputFieldColumn : cns.maxValueInputField}
                    >
                        <span className={classNames(cns.maxValueInputHiddenSpan, inputClassName)} ref={measureSpanRef}>
                            {displayValue}
                        </span>
                        <Input
                            ref={forwardedRef}
                            name={name}
                            inputMode="numeric"
                            {...inputProps}
                            rightElement={
                                displayValue === maxDisplayLabel ? (
                                    <span className={cns.maxValueInputLabelHidden}>{inputProps.rightElement}</span>
                                ) : (
                                    inputProps.rightElement
                                )
                            }
                            value={displayValue}
                            inputWrapClassName={cns.maxValueInputWrapper}
                            inputClassName={classNames(cns.maxValueInputCore, inputClassName)}
                            elementCustomClassName={classNames(
                                cns.maxValueInputCoreRightElement,
                                typeof inputProps.rightElement === "string" && inputProps.disabled && cns.maxValueInputDisabledStringElement
                            )}
                            onBlur={() => {
                                const newValue = displayValue;
                                const valueWithoutWhiteSpace = getWithoutWhiteSpace(newValue);
                                const isValueWithDigitsOnly = isOnlyWithDigits(valueWithoutWhiteSpace);

                                const newValueInt = parseInt(valueWithoutWhiteSpace) | 0;

                                if (!isValueWithDigitsOnly && !warning.isOpen && newValue !== maxDisplayLabel) {
                                    setWarning({
                                        isOpen: true,
                                        message: "Dozwolone są tylko cyfry"
                                    });
                                    setDisplayValue(getNumberFormat(min));
                                    return;
                                }

                                if (isValueWithDigitsOnly && newValueInt < min) {
                                    setWarning({
                                        isOpen: true,
                                        message: `Minimalna wartość: ${min}`
                                    });
                                    setDisplayValue(getNumberFormat(min));
                                    return;
                                }

                                if (isValueWithDigitsOnly && newValueInt > max) {
                                    setWarning({
                                        isOpen: true,
                                        message: `Maksymalna wartość: ${max}`
                                    });
                                    setDisplayValue(maxDisplayLabel || getNumberFormat(max));
                                    return;
                                }
                            }}
                            onChange={(event) => {
                                const input = event.currentTarget;
                                const raw = input.value;
                                const selectionStart = input.selectionStart ?? raw.length;
                                const digitsBeforeCaret = countDigits(raw.slice(0, selectionStart));
                                const digits = raw.replace(/\D/g, "");

                                if (digits === "") {
                                    setDisplayValue("");
                                    return;
                                }

                                const newValueInt = parseInt(digits, 10);

                                if (newValueInt > MAX_VALUE_INPUT_LIMITER) {
                                    setDisplayValue(maxDisplayLabel || getNumberFormat(MAX_VALUE_INPUT_LIMITER));
                                    return;
                                }

                                if (maxDisplayLabel && newValueInt === max) {
                                    setDisplayValue(maxDisplayLabel);
                                    return;
                                }

                                const formatted = getNumberFormat(newValueInt);
                                const caret = getCaretIndexAfterNthDigit(formatted, digitsBeforeCaret);

                                // Sync the DOM value and caret before React reconciles the controlled value,
                                // otherwise reformatting (group separators) resets the caret
                                input.value = formatted;
                                input.setSelectionRange(caret, caret);
                                requestAnimationFrame(() => {
                                    // Samsung keyboard IME can move the selection after the input event
                                    if (document.activeElement === input && input.value === formatted) {
                                        input.setSelectionRange(caret, caret);
                                    }
                                });
                                setDisplayValue(formatted);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.stopPropagation();
                                    event.currentTarget.blur();
                                }
                            }}
                        />
                    </div>
                )}
            </ManualPopover>
        </div>
    );
});

// Utils
function getWithoutWhiteSpace(value: string | number) {
    return value.toString().replace(/\s+/g, "");
}

function isOnlyWithDigits(value: string) {
    return value === "" || /^\d+$/.test(value);
}

function countDigits(value: string) {
    return (value.match(/\d/g) ?? []).length;
}

function getCaretIndexAfterNthDigit(formatted: string, digitCount: number) {
    if (digitCount <= 0) {
        return 0;
    }

    let seen = 0;

    for (let i = 0; i < formatted.length; i++) {
        if (formatted[i] >= "0" && formatted[i] <= "9") {
            seen++;

            if (seen === digitCount) {
                return i + 1;
            }
        }
    }

    return formatted.length;
}
import {CSSProperties, forwardRef, useEffect, useMemo, useRef, useState} from "react";
import classNames from "classnames";

import {ManualPopover} from "@pg-design/floating-module";
import {Range, RangeColorVariant} from "@pg-design/range-module";

import {MAX_VALUE_INPUT_LIMITER} from "../../constants/max_value_input_limiter";
import {IMaxValueInputProps} from "../../types/IMaxValueInputProps";
import {getNumberFormat} from "../../utils/get_number_format";
import {Input} from "../input/Input";

import * as cns from "./MaxValueInput.module.css";

export const MaxValueInput = forwardRef<HTMLInputElement, IMaxValueInputProps>((props, forwardedRef) => {
    const {
        step,
        min,
        max,
        name,
        value,
        onChange,
        inputVariant = "row",
        isDynamicStep = false,
        defaultValue,
        inputClassName,
        maxDisplayLabel,
        centerValue,
        useTriColorSlider,
        ...inputProps
    } = props;

    const [displayValue, setDisplayValue] = useState<string>(getNumberFormat(Number(value || defaultValue || min)));
    const [warning, setWarning] = useState({isOpen: false, message: ""});
    const [inputWidth, setInputWidth] = useState(`${displayValue.toString().length}ch`);

    const warningHideRef = useRef<null | ReturnType<typeof setTimeout>>(null);
    const measureSpanRef = useRef<null | HTMLSpanElement>(null);
    const rangeValue = useMemo(() => {
        if (maxDisplayLabel && displayValue === maxDisplayLabel) {
            return {
                upper: max
            };
        }
        const noWs = getWithoutWhiteSpace(displayValue);

        return {
            upper: parseInt(noWs)
        };
    }, [displayValue]);

    useEffect(() => {
        if (maxDisplayLabel) {
            const numValue = parseInt(getWithoutWhiteSpace(displayValue));
            (numValue === max || numValue === MAX_VALUE_INPUT_LIMITER) && setDisplayValue(maxDisplayLabel);
        }
        const hiddenSpanObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setInputWidth(`${entry.contentRect.width}px`);
            }
        });

        if (measureSpanRef.current) {
            hiddenSpanObserver.observe(measureSpanRef.current);
        }

        return () => {
            hiddenSpanObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (warning.isOpen) {
            warningHideRef.current = setTimeout(() => {
                setWarning({isOpen: false, message: warning.message});
            }, 2000);
        }

        return () => {
            if (warningHideRef.current) {
                clearTimeout(warningHideRef.current);
                warningHideRef.current = null;
            }
        };
    }, [warning.isOpen]);

    useEffect(() => {
        if (onChange) {
            if (displayValue === maxDisplayLabel) {
                onChange(MAX_VALUE_INPUT_LIMITER);
                return;
            }
            const numValue = getWithoutWhiteSpace(displayValue);
            onChange(parseInt(numValue));
        }
    }, [displayValue]);

    return (
        <div
            className={classNames(cns.maxValueInput, inputVariant === "column" && cns.maxValueInputColumn)}
            style={
                {
                    "--input-width": inputWidth
                } as CSSProperties
            }
        >
            <Range
                name={`${name}_range`}
                value={rangeValue}
                useDynamicChange
                onChange={(_, value) => {
                    if (value.upper || value.upper === 0) {
                        maxDisplayLabel && value.upper === max ? setDisplayValue(maxDisplayLabel) : setDisplayValue(getNumberFormat(Number(value.upper)));
                    }
                }}
                step={step}
                min={min}
                max={max}
                className={classNames(cns.maxValueInputRange, inputProps.disabled && cns.maxValueInputRangeDisabled)}
                hideLabels
                disabled={inputProps.disabled}
                centerValue={centerValue}
                useStepChange={isDynamicStep}
                colorVariant={useTriColorSlider ? RangeColorVariant.RED_YELLOW_GREEN : RangeColorVariant.LAVENDER}
            />
            <ManualPopover
                popover={warning.message}
                isOpen={warning.isOpen}
                type="warning"
                handleSetIsOpen={(isWarningOpen) => {
                    setWarning({isOpen: isWarningOpen, message: warning.message});
                }}
                className={cns.maxValueInputPopover}
            >
                {({refs, getReferenceProps}) => (
                    <div
                        ref={refs.setReference}
                        {...getReferenceProps()}
                        className={inputVariant === "column" ? cns.maxValueInputFieldColumn : cns.maxValueInputField}
                    >
                        <span className={classNames(cns.maxValueInputHiddenSpan, inputClassName)} ref={measureSpanRef}>
                            {displayValue}
                        </span>
                        <Input
                            ref={forwardedRef}
                            name={name}
                            inputMode="numeric"
                            {...inputProps}
                            rightElement={
                                displayValue === maxDisplayLabel ? (
                                    <span className={cns.maxValueInputLabelHidden}>{inputProps.rightElement}</span>
                                ) : (
                                    inputProps.rightElement
                                )
                            }
                            value={displayValue}
                            inputWrapClassName={cns.maxValueInputWrapper}
                            inputClassName={classNames(cns.maxValueInputCore, inputClassName)}
                            elementCustomClassName={classNames(
                                cns.maxValueInputCoreRightElement,
                                typeof inputProps.rightElement === "string" && inputProps.disabled && cns.maxValueInputDisabledStringElement
                            )}
                            onBlur={() => {
                                const newValue = displayValue;
                                const valueWithoutWhiteSpace = getWithoutWhiteSpace(newValue);
                                const isValueWithDigitsOnly = isOnlyWithDigits(valueWithoutWhiteSpace);

                                const newValueInt = parseInt(valueWithoutWhiteSpace) | 0;

                                if (!isValueWithDigitsOnly && !warning.isOpen && newValue !== maxDisplayLabel) {
                                    setWarning({
                                        isOpen: true,
                                        message: "Dozwolone są tylko cyfry"
                                    });
                                    setDisplayValue(getNumberFormat(min));
                                    return;
                                }

                                if (isValueWithDigitsOnly && newValueInt < min) {
                                    setWarning({
                                        isOpen: true,
                                        message: `Minimalna wartość: ${min}`
                                    });
                                    setDisplayValue(getNumberFormat(min));
                                    return;
                                }

                                if (isValueWithDigitsOnly && newValueInt > max) {
                                    setWarning({
                                        isOpen: true,
                                        message: `Maksymalna wartość: ${max}`
                                    });
                                    setDisplayValue(maxDisplayLabel || getNumberFormat(max));
                                    return;
                                }
                            }}
                            onChange={(event) => {
                                const input = event.currentTarget;
                                const raw = input.value;
                                const selectionStart = input.selectionStart ?? raw.length;
                                const digitsBeforeCaret = countDigits(raw.slice(0, selectionStart));
                                const digits = raw.replace(/\D/g, "");

                                if (digits === "") {
                                    setDisplayValue("");
                                    return;
                                }

                                const newValueInt = parseInt(digits, 10);

                                if (newValueInt > MAX_VALUE_INPUT_LIMITER) {
                                    setDisplayValue(maxDisplayLabel || getNumberFormat(MAX_VALUE_INPUT_LIMITER));
                                    return;
                                }

                                if (maxDisplayLabel && newValueInt === max) {
                                    setDisplayValue(maxDisplayLabel);
                                    return;
                                }

                                const formatted = getNumberFormat(newValueInt);
                                const caret = getCaretIndexAfterNthDigit(formatted, digitsBeforeCaret);

                                // Sync the DOM value and caret before React reconciles the controlled value,
                                // otherwise reformatting (group separators) resets the caret
                                input.value = formatted;
                                input.setSelectionRange(caret, caret);
                                requestAnimationFrame(() => {
                                    // Samsung keyboard IME can move the selection after the input event
                                    if (document.activeElement === input && input.value === formatted) {
                                        input.setSelectionRange(caret, caret);
                                    }
                                });
                                setDisplayValue(formatted);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.stopPropagation();
                                    event.currentTarget.blur();
                                }
                            }}
                        />
                    </div>
                )}
            </ManualPopover>
        </div>
    );
});

// Utils
function getWithoutWhiteSpace(value: string | number) {
    return value.toString().replace(/\s+/g, "");
}

function isOnlyWithDigits(value: string) {
    return value === "" || /^\d+$/.test(value);
}

function countDigits(value: string) {
    return (value.match(/\d/g) ?? []).length;
}

function getCaretIndexAfterNthDigit(formatted: string, digitCount: number) {
    if (digitCount <= 0) {
        return 0;
    }

    let seen = 0;

    for (let i = 0; i < formatted.length; i++) {
        if (formatted[i] >= "0" && formatted[i] <= "9") {
            seen++;

            if (seen === digitCount) {
                return i + 1;
            }
        }
    }

    return formatted.length;
}
